import * as React from 'react';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { ReportFieldDefinition } from '../../../../models';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import { IconButton } from '@material-ui/core';
import { MultipleCheckboxes, TextInputField } from '../../fields';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import AppButton from '../../../AppButton';

const VALUE_SEPARATOR = '<;>'
const ROW_SEPARATOR = '<n>'
const OPTIONS_SEPARATOR = '|';

interface ReportTableFieldProps {
    id: string,
    value?: string,
    fieldDefinition: ReportFieldDefinition,
    style?: React.CSSProperties,
    readOnly?: boolean,
    onChange?: (value: string) => void,
}

const parseTableValue = (value?: string) => {
    if (!value || !value.length) return []
    if (!value.includes(ROW_SEPARATOR) && !value.includes(VALUE_SEPARATOR)) {
        const legacyRowSeparator = '\n'
        const legacyValueSeparator = ';'
        return value.split(legacyRowSeparator).map(row => row.split(legacyValueSeparator))
    }
    return value.split(ROW_SEPARATOR).map(row => row.split(VALUE_SEPARATOR))
}
const buildTableValue = (data: string[][]) => data.map(row => row.join(VALUE_SEPARATOR)).join(ROW_SEPARATOR)

interface TableRowProps {
    id: string,
    rowIndex: number,
    rowValues: string[],
    tableColumns: string[],
    widths: string[],
    readOnly?: boolean,
    activeColIndex: number | null,
    onCellChange: (rowIndex: number, valueIndex: number, newVal: string) => void,
    onCellActivate: (rowIndex: number, valueIndex: number) => void,
    onCellDeactivate: () => void,
    onTabKey: (rowIndex: number, valueIndex: number, forward: boolean) => void,
    onRemoveRow: (rowIndex: number) => void,
    onAddRow: (rowIndex: number) => void,
}

const cellDisplayStyle: React.CSSProperties = {
    minHeight: '2rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    cursor: 'text',
    padding: '2px',
}

const ReportTableRow: FC<TableRowProps> = memo(({ id, rowIndex, rowValues, tableColumns, widths, readOnly, activeColIndex, onCellChange, onCellActivate, onCellDeactivate, onTabKey, onRemoveRow, onAddRow }) => (
    <tr>
        {tableColumns.map((_, valueIndex) => (
            <td key={valueIndex} style={{width: widths[valueIndex] ? `${widths[valueIndex]}%` : undefined, padding: 'var(--spacer-XXS)'}}>
                {readOnly
                    ? rowValues[valueIndex]
                    : activeColIndex === valueIndex
                        ? <div
                            onBlur={onCellDeactivate}
                            onKeyDown={(e) => {
                                if (e.key === 'Tab') {
                                    e.preventDefault()
                                    onTabKey(rowIndex, valueIndex, !e.shiftKey)
                                }
                            }}
                          >
                            <TextInputField
                                type='textarea'
                                id={`${id}-table-row-${rowIndex}-value-${valueIndex}`}
                                value={rowValues[valueIndex]}
                                onChange={(newVal: string) => onCellChange(rowIndex, valueIndex, newVal)}
                                autoFocus
                            />
                          </div>
                        : <div
                            style={cellDisplayStyle}
                            onClick={() => onCellActivate(rowIndex, valueIndex)}
                        >
                            {rowValues[valueIndex]}
                        </div>
                }
            </td>
        ))}
        {!readOnly
            ? <th style={{padding: 'var(--spacer-XXS)', whiteSpace: 'nowrap'}}>
                <IconButton
                    id={`${id}-table-row-${rowIndex}-remove-button`}
                    onClick={() => onRemoveRow(rowIndex)}
                    size="small"
                >
                    <RemoveCircleIcon/>
                </IconButton>
                <IconButton
                    id={`${id}-table-row-${rowIndex}-add-button`}
                    onClick={() => onAddRow(rowIndex + 1)}
                    size="small"
                >
                    <AddCircleIcon/>
                </IconButton>
            </th>
            : undefined}
    </tr>
), (prev, next) => {
    if (prev.rowIndex !== next.rowIndex || prev.readOnly !== next.readOnly || prev.id !== next.id) return false
    if (prev.activeColIndex !== next.activeColIndex) return false
    if (prev.rowValues.length !== next.rowValues.length) return false
    for (let i = 0; i < prev.rowValues.length; i++) {
        if (prev.rowValues[i] !== next.rowValues[i]) return false
    }
    return true
})

export const ReportTableField: FC<ReportTableFieldProps> = ({
    id,
    value,
    readOnly,
    style,
    fieldDefinition: {label, aditionalFields},
    onChange,
}) => {
    const t = useTranslations()

    const emptyOption = useMemo(() => t(TK.empty), [t])
    const tableColumns = useMemo(() => aditionalFields?.columns?.split('|') ?? [], [aditionalFields?.columns])
    const widths = useMemo(() => aditionalFields?.widths?.split('|') ?? [], [aditionalFields?.widths])
    const tableValues = useMemo(() => parseTableValue(value), [value])
    const options = useMemo(() => aditionalFields?.options?.length ? aditionalFields.options.split(OPTIONS_SEPARATOR) : [], [aditionalFields])

    const [addMultipleValues, setAddMultipleValues] = useState<undefined|string[]>(undefined)
    const [activeCell, setActiveCell] = useState<{ rowIndex: number; valueIndex: number } | null>(null)

    // Refs so stable callbacks always see latest values without being recreated
    const tableValuesRef = useRef(tableValues)
    tableValuesRef.current = tableValues
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const tableColumnsRef = useRef(tableColumns)
    tableColumnsRef.current = tableColumns

    const handleTableChange = useCallback((rowIndex: number, valueIndex: number, newVal: string) => {
        const updated = tableValuesRef.current.map((row, rIdx) =>
            rIdx === rowIndex ? row.map((v, vIdx) => vIdx === valueIndex ? newVal : v) : row
        )
        onChangeRef.current?.(buildTableValue(updated))
    }, [])

    const handleRemoveRow = useCallback((rowIndex: number) => {
        onChangeRef.current?.(buildTableValue(tableValuesRef.current.filter((_, i) => i !== rowIndex)))
    }, [])

    const handleAddRow = useCallback((rowIndex: number) => {
        const updated = [...tableValuesRef.current]
        updated.splice(rowIndex, 0, Array(tableColumnsRef.current.length).fill(''))
        onChangeRef.current?.(buildTableValue(updated))
    }, [])

    const handleCellActivate = useCallback((rowIndex: number, valueIndex: number) => {
        setActiveCell({ rowIndex, valueIndex })
    }, [])

    const isNavigatingRef = useRef(false)

    const handleCellDeactivate = useCallback(() => {
        if (isNavigatingRef.current) {
            isNavigatingRef.current = false
            return
        }
        setActiveCell(null)
    }, [])

    const handleTabKey = useCallback((rowIndex: number, valueIndex: number, forward: boolean) => {
        const totalCols = tableColumnsRef.current.length
        const totalRows = tableValuesRef.current.length

        let nextRow = rowIndex
        let nextCol = forward ? valueIndex + 1 : valueIndex - 1

        if (nextCol >= totalCols) {
            nextCol = 0
            nextRow = rowIndex + 1
        } else if (nextCol < 0) {
            nextCol = totalCols - 1
            nextRow = rowIndex - 1
        }

        if (nextRow >= 0 && nextRow < totalRows) {
            isNavigatingRef.current = true
            setActiveCell({ rowIndex: nextRow, valueIndex: nextCol })
        }
    }, [])

    const handleAddMultipleValues = useCallback(() => {
        const newRows = addMultipleValues?.map(option => option === emptyOption
            ? Array(tableColumnsRef.current.length).join(VALUE_SEPARATOR)
            : option).join(ROW_SEPARATOR)
        onChangeRef.current && newRows && onChangeRef.current(value?.length ? `${value}${ROW_SEPARATOR}${newRows}` : newRows)
        setAddMultipleValues(undefined)
    }, [value, addMultipleValues, emptyOption])

    return (<>
        <Table id={`${id}-table`} style={style} striped={readOnly}>
            <thead>
                <tr>
                    {tableColumns.map((c, i) => (<th key={c} style={{whiteSpace: 'pre', width: widths[i] ? `${widths[i]}%` : undefined, padding: 'var(--spacer-XXS)'}}>{c}</th>))}
                    {!readOnly ? <th><IconButton id={`${id}-table-add-button`} onClick={() => handleAddRow(0)} size="small"><AddCircleIcon/></IconButton></th> : undefined}
                </tr>
            </thead>
            <tbody>{tableValues.map((rowValues, rowIndex) =>
                <ReportTableRow
                    key={rowIndex}
                    id={id}
                    rowIndex={rowIndex}
                    rowValues={rowValues}
                    tableColumns={tableColumns}
                    widths={widths}
                    readOnly={readOnly}
                    activeColIndex={activeCell?.rowIndex === rowIndex ? activeCell.valueIndex : null}
                    onCellChange={handleTableChange}
                    onCellActivate={handleCellActivate}
                    onCellDeactivate={handleCellDeactivate}
                    onTabKey={handleTabKey}
                    onRemoveRow={handleRemoveRow}
                    onAddRow={handleAddRow}
                />
            )}
            </tbody>
        </Table>

        <Modal isOpen={addMultipleValues !== undefined} toggle={() => setAddMultipleValues(undefined)}>
            <ModalHeader>{label}</ModalHeader>
            <ModalBody>
                <MultipleCheckboxes id={`${id}-multiple-add-values`} options={[emptyOption, ...options]} values={addMultipleValues} onChange={setAddMultipleValues}/>
            </ModalBody>
            <ModalFooter>
                <AppButton id={`${id}-multiple-add-button`} onClick={handleAddMultipleValues} color="success">{t(TK.add)}</AppButton>
            </ModalFooter>
        </Modal>
    </>)
}
